/**
 * GeoJSON loading utilities with Cache API support
 *
 * Handles loading GeoJSON from URLs or files, with automatic caching
 * for offline access. Copied from V1 with minor cleanup.
 */

import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
} from "geojson";

/**
 * Custom error for offline scenarios
 */
export class OfflineError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "OfflineError";
  }
}

/**
 * Check if browser is online
 */
function isOnline(): boolean {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine;
}

/**
 * Type guard for FeatureCollection
 */
function isFeatureCollection(
  value: unknown
): value is FeatureCollection<Geometry, GeoJsonProperties> {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as FeatureCollection).type === "FeatureCollection" &&
    Array.isArray((value as FeatureCollection).features)
  );
}

/**
 * Read GeoJSON from uploaded file
 */
export async function readGeoJSONFile(
  file: File
): Promise<FeatureCollection<Geometry, GeoJsonProperties>> {
  const text = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read GeoJSON file"));
    reader.onabort = () => reject(new Error("GeoJSON file reading aborted"));
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("GeoJSON file result was not text"));
      }
    };
    reader.readAsText(file);
  });

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    throw new Error("The uploaded file is not valid JSON");
  }

  if (!isFeatureCollection(parsed)) {
    throw new Error("Expected a GeoJSON FeatureCollection");
  }

  return parsed;
}

/**
 * Fetch GeoJSON from URL with Cache API support
 *
 * This uses the browser's Cache API to store GeoJSON files for offline access.
 * Once cached, the file will be available even when offline.
 */
export async function fetchGeoJSONFromUrl(
  url: string
): Promise<FeatureCollection<Geometry, GeoJsonProperties>> {
  const absoluteUrl =
    typeof window !== "undefined"
      ? new URL(url, window.location.origin).toString()
      : url;

  const parseResponse = async (response: Response) => {
    const data = await response.clone().json();
    if (!isFeatureCollection(data)) {
      throw new Error("Downloaded resource is not a FeatureCollection");
    }
    return data;
  };

  // Use Cache API if available
  if (typeof window !== "undefined" && "caches" in window) {
    const cache = await caches.open("mapquizzr-geojson");
    const cached = await cache.match(absoluteUrl);

    // Try cache first
    if (cached) {
      try {
        console.log("[GeoJSON] Serving from cache:", absoluteUrl);
        return await parseResponse(cached);
      } catch (error) {
        console.warn("Failed to read cached GeoJSON, fetching fresh copy", error);
      }
    }

    // Try to fetch from network
    try {
      const response = await fetch(absoluteUrl, { cache: "no-store" });

      if (!response.ok) {
        throw new Error(`Unable to download GeoJSON (${response.status})`);
      }

      const data = await parseResponse(response);

      // Cache the successful response
      try {
        await cache.put(absoluteUrl, response.clone());
        console.log("[GeoJSON] Cached successfully:", absoluteUrl);
      } catch (error) {
        console.warn("Unable to cache GeoJSON response", error);
      }

      return data;
    } catch (error) {
      // Network error - check if we're offline
      if (!isOnline()) {
        throw new OfflineError(
          "You're offline and this map hasn't been cached. Please connect to the internet to download it."
        );
      }

      // Other error
      throw error;
    }
  }

  // Fallback for environments without Cache API
  const response = await fetch(absoluteUrl);
  if (!response.ok) {
    throw new Error(`Unable to download GeoJSON (${response.status})`);
  }
  const data = await response.json();
  if (!isFeatureCollection(data)) {
    throw new Error("Downloaded resource is not a FeatureCollection");
  }
  return data;
}

/**
 * Extract all property keys from a FeatureCollection
 */
export function extractPropertyKeys(
  collection: FeatureCollection<Geometry, GeoJsonProperties>
): string[] {
  const keys = new Set<string>();
  for (const feature of collection.features) {
    const properties = feature.properties ?? {};
    Object.keys(properties).forEach((key) => {
      if (key.trim().length > 0) {
        keys.add(key);
      }
    });
  }
  return Array.from(keys).sort((a, b) => a.localeCompare(b));
}

/**
 * Get stable identifier for a feature
 *
 * IMPORTANT: This must return the same ID for the same feature across
 * filter operations. Use original index, not filtered index!
 */
export function getFeatureIdentifier(
  feature: Feature<Geometry, GeoJsonProperties>,
  index: number
): string {
  if (feature.id !== undefined && feature.id !== null) {
    return String(feature.id);
  }

  const properties = feature.properties ?? {};
  const fallbackKeys = ["id", "code", "code_insee", "areaCode", "name"];
  for (const key of fallbackKeys) {
    if (properties[key] !== undefined && properties[key] !== null) {
      return String(properties[key]);
    }
  }

  return `feature-${index}`;
}

/**
 * Get human-readable label for a feature
 */
export function getFeatureLabel(
  feature: Feature<Geometry, GeoJsonProperties>
): string {
  const properties = feature.properties ?? {};
  const preferredKeys = ["name", "nom", "region", "title", "label"];
  for (const key of preferredKeys) {
    if (properties[key]) {
      return String(properties[key]);
    }
  }
  return feature.id ? String(feature.id) : "Untitled feature";
}

/**
 * Clear a specific GeoJSON URL from the cache
 */
export async function clearGeoJSONFromCache(url: string): Promise<void> {
  if (typeof window === "undefined" || !("caches" in window)) {
    return;
  }

  const absoluteUrl = new URL(url, window.location.origin).toString();
  const cache = await caches.open("mapquizzr-geojson");
  await cache.delete(absoluteUrl);
}

/**
 * Clear all GeoJSON files from the cache
 */
export async function clearAllGeoJSONCache(): Promise<void> {
  if (typeof window === "undefined" || !("caches" in window)) {
    return;
  }

  await caches.delete("mapquizzr-geojson");
}
